/**
 * LegalPage — Reusable layout for legal/policy pages (privacy, terms, etc.).
 *
 * Renders markdown content with:
 * - SEO tags (title, description, canonical, OG, JSON-LD WebPage)
 * - Gradient page title + "Last updated" subtitle
 * - Styled markdown body via react-markdown
 *
 * Navbar, background, and footer are provided by PublicLayout.
 */

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Box, Container, Text, Stack } from '@mantine/core';
import { SEO } from './SEO';

interface LegalPageProps {
  /** Page title shown as gradient heading and in SEO tags */
  title: string;
  /** Human-readable last-updated date, e.g. "February 16, 2026" */
  lastUpdated: string;
  /** Raw markdown string to render */
  content: string;
  /** Canonical path for SEO, e.g. "/privacy" */
  seoPath: string;
  /** Meta description for SEO */
  seoDescription: string;
}

const CSS = `
  .legal-content h2 {
    font-size: 1.35rem;
    font-weight: 700;
    color: #fff;
    margin-top: 2.5rem;
    margin-bottom: 0.75rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid rgba(255,255,255,.06);
  }
  .legal-content h3 {
    font-size: 1.1rem;
    font-weight: 600;
    color: rgba(255,255,255,.85);
    margin-top: 1.75rem;
    margin-bottom: 0.5rem;
  }
  .legal-content p {
    color: rgba(255,255,255,.65);
    line-height: 1.8;
    margin-bottom: 1rem;
  }
  .legal-content ul, .legal-content ol {
    color: rgba(255,255,255,.65);
    padding-left: 1.5rem;
    margin-bottom: 1rem;
  }
  .legal-content li {
    margin-bottom: 0.4rem;
    line-height: 1.7;
  }
  .legal-content a {
    color: #fd7e14;
    text-decoration: none;
    border-bottom: 1px solid rgba(253,126,20,.3);
    transition: border-color .2s;
  }
  .legal-content a:hover {
    border-bottom-color: #fd7e14;
  }
  .legal-content hr {
    border: none;
    border-top: 1px solid rgba(255,255,255,.06);
    margin: 2rem 0;
  }
  .legal-content strong {
    color: rgba(255,255,255,.85);
    font-weight: 600;
  }
`;

const gradientText: React.CSSProperties = {
  background: 'linear-gradient(135deg, #fd7e14, #ff922b)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
};

export function LegalPage({
  title,
  lastUpdated,
  content,
  seoPath,
  seoDescription,
}: LegalPageProps) {
  return (
    <Box style={{ position: 'relative', zIndex: 1 }}>
      <SEO
        title={title}
        description={seoDescription}
        path={seoPath}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          'name': `${title} | Haive`,
          'url': `https://haive.dev${seoPath}`,
          'description': seoDescription,
          'isPartOf': {
            '@type': 'WebSite',
            'name': 'Haive',
            'url': 'https://haive.dev',
          },
        }}
      />
      <style>{CSS}</style>

      <Container size="md" pt={120} pb={80}>
        <Stack gap="xs" mb={40}>
          <Text component="h1" fw={800} fz={36} style={gradientText}>
            {title}
          </Text>
          <Text size="sm" c="dimmed" style={{ opacity: 0.5 }}>
            Last updated: {lastUpdated}
          </Text>
        </Stack>

        <Box className="legal-content">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {content}
          </ReactMarkdown>
        </Box>
      </Container>
    </Box>
  );
}
